import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  model,
  OnInit,
  signal,
} from '@angular/core';
import { DateTime } from 'luxon';
import { SbLineChartModel } from '@ngx-seatback/charts';
import { SbDateTime, SbLocalDateTime } from 'src/app/services/api/api-date-time';
import { API, CampaignWithPreviewDTO, CommercialWithPreviewDTO, LocalizedFramedReportingDTO } from '../../services/api/api';
import { ErrorService } from '../../services/error.service';
import { UtilService } from '../../services/util.service';
import { CampaignWithPreviewReportingDTO, CommercialWithPreviewReportingDTO } from '../../models/campaigns.model';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { TranslateService } from '@ngx-translate/core';
import { TagService } from '../../services/tag.service';
import { UserService } from '../../services/user.service';
import { PermissionService } from '../../services/permission.service';
import { Observable, Subscription } from 'rxjs';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';

type CCRMode = 'campaign' | 'commercial' | null;

@Component({
  selector: 'sb-comp-ccr-hero',
  templateUrl: './ccr-hero.component.html',
  styleUrls: ['./ccr-hero.component.scss'],
  animations: [
    trigger('smoothCollapse', [
      state(
        'initial',
        style({
          height: '0',
          overflow: 'hidden',
          opacity: '0',
          'padding-top': '0',
          'padding-bottom': '0',
        })
      ),
      state(
        'final',
        style({
          opacity: '1',
        })
      ),
      transition('initial=>final', animate('200ms')),
      transition('final=>initial', animate('200ms')),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CcrHeroComponent implements OnInit {
  protected readonly destroyRef = inject(DestroyRef);
  private readonly api = inject(API);
  public readonly errorHandler = inject(ErrorService);
  public readonly util = inject(UtilService);
  private readonly activatedRoute = inject(ActivatedRoute);
  public readonly translate = inject(TranslateService);
  public readonly tagService = inject(TagService);
  public readonly userStatus = inject(UserService);
  public readonly cdRef = inject(ChangeDetectorRef);
  public readonly permissionService = inject(PermissionService);

  ccr = input.required<CampaignWithPreviewReportingDTO | CommercialWithPreviewReportingDTO>();
  isCollapsed = model<boolean>(false);

  ccrMode = computed<CCRMode>(() => {
    const ccr = this.ccr();
    if (ccr instanceof CampaignWithPreviewDTO) {
      return 'campaign';
    }
    if (ccr instanceof CommercialWithPreviewDTO) {
      return 'commercial';
    }
    return null;
  });

  ccrIcon = computed(() => {
    switch (this.ccrMode()) {
      case 'campaign': {
        return 'megaphone';
      }
      case 'commercial': {
        return 'commercial';
      }
      case null:
      default: {
        return 'list';
      }
    }
  });

  // chart variable
  lineChartGradient: SbLineChartModel = {
    title: 'Playout Statistics',
    height: 150,
    value: 'value',
    category: 'day',
    fill: true,
    gradient: true,
    loadingDone: false,
    valueAssignments: [
      {
        value: 'value',
        categories: ['day'],
        valueName: 'Playouts',
        color: '#016aa2',
      },
    ],
    data: [],
  };
  // request variables
  loading = signal(true);
  subs: Subscription[] = [];
  timer: ReturnType<typeof setTimeout> = null;

  // redirection variables
  campaignId = null;
  commercialId = null;

  // user changeable variables
  protected from = new SbLocalDateTime(DateTime.utc().startOf('day').minus({ day: 30 }));
  protected till = new SbLocalDateTime(DateTime.utc().endOf('day').minus({ day: 1 }));
  protected interval: number = 86400; // Day in seconds

  ngOnInit(): void {
    this.activatedRoute.params.subscribe(params => {
      this.campaignId = +params.campaignID;
      this.commercialId = +params.commercialID;
    });
    if (!this.isCollapsed()) {
      this.requestDebounced();
    }
  }

  displayData() {
    this.isCollapsed.set(!this.isCollapsed());
    this.requestDebounced();
  }

  refreshFromServer() {
    if (this.lineChartGradient) {
      this.lineChartGradient.loadingDone = false;
    }

    if (!this.from) {
      this.from = new SbLocalDateTime(DateTime.utc().startOf('day').minus({ day: 30 }));
    }
    if (!this.till) {
      this.till = new SbLocalDateTime(DateTime.utc().endOf('day').minus({ day: 1 }));
    }
    if (!this.interval) {
      this.interval = 86400;
    }
    let obs: Observable<LocalizedFramedReportingDTO>;
    const currentCCR = this.ccr();
    if (!currentCCR.id) {
      throw new Error('Missing CCR id');
    }
    if (
      this.isCollapsed() ||
      this.lineChartGradient.loadingDone ||
      !this.permissionService.hasPermission(this.permissionService.Permission.ds_cm_reporting)
    ) {
      return;
    }

    switch (this.ccrMode()) {
      case 'campaign': {
        obs = this.api.ds_cm_Reporting_campaignsReport({
          id: [currentCCR.id],
          interval: this.interval,
          from: this.from,
          till: this.till,
        });
        break;
      }
      case 'commercial': {
        obs = this.api.ds_cm_Reporting_commercialsReport({
          id: [currentCCR.id],
          interval: this.interval,
          from: this.from,
          till: this.till,
        });
        break;
      }
      case null:
      default:
        throw new Error('Missing CCR Mode');
    }
    obs
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loading.set(false))
      )
      .subscribe({
        next: data => {
          const chartData = [];
          data.buckets[0].frames.forEach(frame => {
            chartData.push({
              day: frame.range.from.value.toFormat(this.interval < 80000 ? 'HH:mm dd.MM' : 'dd.MM'),
              value: frame.data.productivePlayouts,
            });
          });
          this.lineChartGradient.data = chartData;
          this.lineChartGradient.loadingDone = true;
          this.cdRef.detectChanges();
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  protected dateChanged(dates: { fromDate: SbDateTime; toDate: SbDateTime; interval: number }) {
    if (dates?.fromDate && dates?.toDate) {
      this.from = new SbLocalDateTime(dates.fromDate.value);
      this.till = new SbLocalDateTime(dates.toDate.value);
      this.requestDebounced();
    }
  }

  requestDebounced() {
    this.loading.set(true);

    if (this.timer) {
      clearTimeout(this.timer);
    }

    this.subs.forEach(sub => sub.unsubscribe());
    this.subs = [];

    this.timer = setTimeout(() => {
      this.refreshFromServer();
    }, 500);
  }

  protected identifyCampaign(
    currentCCR: CampaignWithPreviewReportingDTO | CommercialWithPreviewReportingDTO
  ): CampaignWithPreviewReportingDTO {
    return currentCCR as CampaignWithPreviewReportingDTO;
  }
  protected identifyCommercial(
    currentCCR: CampaignWithPreviewReportingDTO | CommercialWithPreviewReportingDTO
  ): CommercialWithPreviewReportingDTO {
    return currentCCR as CommercialWithPreviewReportingDTO;
  }
}
